import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Gamepad2, Dice5, Smartphone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FunnyLoader } from "@/components/FunnyLoader";
import { MetaTags } from "@/components/MetaTags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gameTypeIcons = {
  videogame: Gamepad2,
  boardgame: Dice5,
  mobile: Smartphone,
};

const gameTypeLabels = {
  videogame: "Videojuego",
  boardgame: "Juego de Mesa",
  mobile: "Móvil",
};

const statusLabels = {
  coming_soon: "Próximamente",
  new: "¡Nuevo!",
  available: "Disponible",
  early_access: "Early Access",
};

const statusColors = {
  coming_soon: "bg-yellow-500 text-black",
  new: "bg-[#E91E8C] text-white",
  available: "bg-green-500 text-white",
  early_access: "bg-blue-500 text-white",
};

export default function GamesListPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await axios.get(`${API}/games`);
        setGames(res.data.filter(g => g.active));
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <FunnyLoader />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="games-list-page">
      <MetaTags 
        title="Juegos"
        description="Descubre los juegos de Darkar Company"
        url="/juegos"
      />
      <Navbar />
      
      <div className="h-[80px]" />

      <main className="flex-1 px-4 md:px-8 py-8 pt-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Page Title - Same style as Shows */}
          <div className="flex justify-center mb-10">
            <h1 
              className="w-full text-center px-8 py-3 bg-black text-white text-2xl md:text-3xl font-black uppercase tracking-wide hand-drawn-subtle"
              style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
            >
              Juegos
            </h1>
          </div>

          {games.length === 0 ? (
            <div className="text-center py-20 px-4">
              <div className="text-6xl mb-4">🎮</div>
              <p className="text-white text-xl mb-2 font-bold">
                ¡Próximamente tendremos juegos!
              </p>
              <p className="text-gray-400 text-sm">
                Estamos trabajando en algo increíble
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-testid="games-grid">
              {games.map((game) => {
                const TypeIcon = gameTypeIcons[game.game_type] || Gamepad2;
                
                return (
                  <Link
                    key={game.id}
                    to={`/juegos/${game.slug || game.id}`}
                    className="group relative overflow-hidden hand-drawn-subtle transition-transform hover:scale-105"
                    style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    data-testid={`game-card-${game.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-[3/4] bg-black relative overflow-hidden">
                      {game.thumbnail_url ? (
                        <img 
                          src={game.thumbnail_url}
                          alt={game.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2a2a2a] to-[#0a0a0a]">
                          <TypeIcon size={48} className="text-white/30" />
                        </div>
                      )}
                      
                      {/* Status Badge */}
                      {game.status && game.status !== 'available' && (
                        <div className={`absolute top-2 right-2 px-2 py-1 text-xs font-black uppercase ${statusColors[game.status]}`}>
                          {statusLabels[game.status]}
                        </div>
                      )}
                      
                      {/* Game Type Badge */}
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/80 text-white text-xs font-bold flex items-center gap-1">
                        <TypeIcon size={12} />
                        {gameTypeLabels[game.game_type]}
                      </div>
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white font-black uppercase text-sm">Ver más</span>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <div className="p-3 bg-black">
                      <h3 className="text-white font-bold text-sm uppercase truncate">{game.title}</h3>
                      {game.tagline && (
                        <p className="text-white/60 text-xs truncate mt-1">{game.tagline}</p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
